/*
  Safir tap feedback. Tiny, no libraries.

  - Every button and button-style link presses in when touched, then jumps
    up a few pixels and lands with a small bounce. Pill buttons also send
    out a soft ring.
  - Product cards and flavor tiles: the photo sinks a little while it is
    pressed, then pops up with a ring in the product's flavor color and a
    brighter, slightly zoomed picture as the product page opens.
  - Picking an option (pack size, flavor, delivery plan) gives the choice a
    hop; flavor choices add a splash in that flavor's color, and the photo
    bobs as the new flavor comes in. Changing a pack size on a card ticks
    the price over.
  - After an add to cart, the cart count pops and the photo of the product
    that was added does a little cheer.

  Motion uses the individual `scale` and `translate` CSS properties, so it
  stacks on top of any hover `transform` an element already has instead of
  replacing it. Nothing moves for people whose device asks for reduced
  motion. Styles: "Tap feedback" in assets/sr-base.css (splash) and
  assets/sx-feel.css (jump, ring, lift, tick).
*/
(() => {
  const root = document.documentElement;
  const motion = window.matchMedia('(prefers-reduced-motion: no-preference)');

  const TAP = [
    'button',
    '.button',
    '[role="button"]',
    'input[type="submit"]',
    '.sr-btn',
    '.sr-chip',
    '.sg-btn',
    '.sx-btn',
    '.sx-link',
    '.sx-hero__chips a',
    '.sx-chiprow a',
    '.sx-pagination a',
    '.sg-header-cta',
    '.header-actions__action',
    '.menu-list__link',
    'summary',
    '.shopify-payment-button__button',
  ].join(',');
  // Buttons that also send out a ring when tapped.
  const RING = [
    '.button',
    '.sr-btn',
    '.sr-chip',
    '.sg-btn',
    '.sx-btn',
    '.sx-tab',
    '.sx-hero__chips a',
    '.sx-chiprow a',
    '.sg-header-cta',
    '.shopify-payment-button__button',
  ].join(',');
  // Radio options: buy box cards and card pack sizes. They are animated on
  // 'change', because the buy box redraws them when the choice changes.
  const CARD = '.sg-opt, .sx-size';
  // Product tiles whose photo pops when they are opened, and the part of
  // each tile that opens the product.
  const TILE = '.sx-card, .sx-flavor, .product-card';
  const TILE_HIT = '.sx-card__media, .sx-card__title a, .sx-flavor, .product-card a';
  const TILE_MEDIA = '.sx-card__media, .sx-flavor__media, .card-gallery, .product-card__media';
  // Accordion rows are full width; their icon already answers the tap.
  const SKIP = '.sx-acc > summary, .sg-acc > summary';
  const FLAVORS = ['pina', 'strawberry', 'passion'];
  const ANIMS = ['sr-press', 'sr-pop', 'sr-ring', 'sr-hop', 'sr-bob', 'sr-sink', 'sr-lift', 'sr-cheer', 'sr-tick'];

  const isOff = (el) =>
    el.matches(':disabled, [aria-disabled="true"], .is-disabled') || el.closest('[data-sr-no-tap]');

  const replay = (el, ...cls) => {
    el.classList.remove(...ANIMS);
    void el.offsetWidth; // restart the animation if it is already running
    el.classList.add(...cls);
    // A button that is hidden mid-animation (a drawer closing) never gets
    // animationend; clear it anyway so it doesn't jump when shown again.
    setTimeout(() => {
      if (!el.getAnimations || !el.getAnimations().some((a) => a.playState === 'running')) {
        el.classList.remove(...cls);
      }
    }, 900);
  };

  document.addEventListener('animationend', (event) => {
    const el = event.target;
    if (!event.animationName.startsWith('sr-tap-') || !(el instanceof Element)) return;
    // An element can run two animations at once (jump and ring); clean up
    // after the last one.
    if (el.getAnimations && el.getAnimations().some((a) => a.playState === 'running')) return;
    el.classList.remove(...ANIMS);
  });

  // Same physical squash for every size: a chip dips about 6%, a full-width
  // bar about 1.5%, so big buttons never look like they are collapsing.
  const size = (el) => {
    const w = el.getBoundingClientRect().width || 100;
    const dip = Math.min(0.06, Math.max(0.015, 7 / w));
    el.style.setProperty('--sr-press', String(1 - dip));
    el.style.setProperty('--sr-over', String(1 + dip / 2));
    el.style.setProperty('--sr-jump', w > 280 ? '-3px' : '-5px');
  };

  /* ---- Flavor splash ------------------------------------------------- */

  const color = (flavor) => getComputedStyle(root).getPropertyValue(`--sr-${flavor}`).trim();

  const flavorOf = (el) => {
    if (el.dataset.srFlavorSet) return el.dataset.srFlavorSet;
    const text = el.textContent.toLowerCase();
    if (text.includes('colada')) return 'pina';
    if (text.includes('strawberry')) return 'strawberry';
    if (text.includes('passion')) return 'passion';
    return null; // variety: all three colors
  };

  const isFlavor = (el) => el.matches('[data-sr-flavor-set], .sg-flavor, .sx-hero__chips a');

  // Where the splash starts: the color dot on a chip, the bottle photo on a
  // flavor card, otherwise the middle of the element.
  const origin = (el) => {
    const swatch = el.querySelector('.sg-flavor__img, .sx-hero__chips i');
    if (swatch) {
      const r = swatch.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }
    const r = el.getBoundingClientRect();
    if (el.matches('.sr-chip')) {
      return { x: r.left + parseFloat(getComputedStyle(el).paddingLeft) + 7, y: r.top + r.height / 2 };
    }
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  };

  const splash = (el) => {
    const flavor = flavorOf(el);
    const { x, y } = origin(el);
    const node = document.createElement('span');
    node.className = 'sr-splash';
    node.setAttribute('aria-hidden', 'true');
    node.style.left = `${x}px`;
    node.style.top = `${y}px`;
    node.style.setProperty('--sr-splash', flavor ? color(flavor) : 'var(--sr-ink)');
    for (let i = 0; i < 6; i += 1) {
      const drop = document.createElement('i');
      drop.style.setProperty('--a', `${i * 60 - 30}deg`);
      if (!flavor) drop.style.background = color(FLAVORS[i % 3]);
      node.append(drop);
    }
    document.body.append(node);
    setTimeout(() => node.remove(), 800);
  };

  const activate = (el) => {
    size(el);
    if (isFlavor(el)) {
      splash(el); // measured before the hop moves it
      replay(el, 'sr-hop');
    } else if (el.matches(RING)) {
      replay(el, 'sr-pop', 'sr-ring');
    } else {
      replay(el, 'sr-pop');
    }
  };

  /* ---- Product tiles ------------------------------------------------- */

  const mediaOf = (tile) => tile.querySelector(TILE_MEDIA);

  const tileFor = (target) => {
    const hit = target.closest(TILE_HIT);
    if (!hit || target.closest('form, .sx-card__buy, button')) return null;
    const tile = hit.closest(TILE);
    return tile && mediaOf(tile) ? tile : null;
  };

  /* ---- Press, release, activate ------------------------------------- */

  let pressed = null;
  let pressedTile = null;
  let pressTimer = 0;

  const release = () => {
    clearTimeout(pressTimer);
    if (pressed) pressed.classList.remove('sr-press');
    if (pressedTile) mediaOf(pressedTile)?.classList.remove('sr-sink');
    pressed = null;
    pressedTile = null;
  };

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!motion.matches || event.button !== 0 || !(event.target instanceof Element)) return;
      release();
      const tile = tileFor(event.target);
      const el = tile ? null : event.target.closest(`${TAP},${CARD},.sx-tab`);
      if (!tile && (!el || isOff(el) || el.matches(SKIP))) return;
      if (el) size(el);
      pressed = el;
      pressedTile = tile;
      // On phones, wait a beat so starting a scroll on a button doesn't
      // flash the press.
      const press = () => {
        if (el) el.classList.add('sr-press');
        if (tile) mediaOf(tile)?.classList.add('sr-sink');
      };
      if (event.pointerType === 'touch') pressTimer = setTimeout(press, 60);
      else press();
    },
    { passive: true }
  );

  document.addEventListener(
    'pointerup',
    (event) => {
      const el = pressed;
      const tile = pressedTile;
      release();
      if (!motion.matches || !(event.target instanceof Element)) return;
      if (tile && tile.contains(event.target)) {
        const media = mediaOf(tile);
        if (media) replay(media, 'sr-lift');
        tile.querySelectorAll('.sx-flavor__cta').forEach((cta) => {
          size(cta);
          replay(cta, 'sr-pop');
        });
        return;
      }
      if (!el || el.matches(CARD)) return;
      if (el.contains(event.target)) {
        activate(el);
        noteAdd(el);
        if (el.matches('.sg-buy__thumb')) {
          const photo = el.closest('[data-sg-buy]')?.querySelector('.sg-buy__main');
          if (photo) replay(photo, 'sr-bob');
        }
      }
    },
    { passive: true }
  );
  document.addEventListener('pointercancel', release, { passive: true });
  document.addEventListener('dragstart', release, { passive: true });

  // Keyboard presses (Enter / Space) arrive as clicks with detail 0.
  document.addEventListener('click', (event) => {
    if (event.detail !== 0 || !motion.matches || !(event.target instanceof Element)) return;
    const el = event.target.closest(`${TAP},.sx-tab`);
    if (el && !el.closest(CARD) && !isOff(el) && !el.matches(SKIP)) {
      activate(el);
      noteAdd(el);
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!motion.matches || !(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    let card = input.isConnected ? input.closest(CARD) : null;
    if (!card && input.name) {
      const live = document.querySelector(
        `input[type="radio"][name="${CSS.escape(input.name)}"][value="${CSS.escape(input.value)}"]`
      );
      card = live && live.closest(CARD);
    }
    if (!card || isOff(card)) return;
    activate(card);
    if (card.matches('.sg-flavor')) {
      const photo = card.closest('[data-sg-buy]')?.querySelector('.sg-buy__main');
      if (photo) replay(photo, 'sr-bob');
    }
    // Pack size on a product card: the price and per-unit line tick over.
    if (card.matches('.sx-size')) {
      card
        .closest('[data-sx-card]')
        ?.querySelectorAll('[data-sx-price], [data-sx-per]')
        .forEach((line) => replay(line, 'sr-tick'));
    }
  });

  /* ---- Hero photo follows the flavor -------------------------------- */

  document.addEventListener('sr:flavor', (event) => {
    if (!motion.matches) return;
    document.querySelectorAll('.sr-hero__media').forEach((media) => replay(media, 'sr-bob'));
    document
      .querySelectorAll(`.sr-hero__cta[data-flavor="${event.detail.flavor}"]`)
      .forEach((cta) => replay(cta, 'sr-pop'));
  });

  /* ---- After an add to cart ----------------------------------------- */

  // Remember which product's add button was pressed, so its photo can
  // cheer once the cart count actually goes up.
  let lastAdd = null;
  function noteAdd(el) {
    if (!el.matches('button[type="submit"], input[type="submit"]') || !el.form) return;
    if (!/\/cart\/add/.test(el.form.getAttribute('action') || '')) return;
    const holder = el.closest('[data-sx-card], [data-sg-buy], .sx-bundle, .product-card');
    const photo = holder && holder.querySelector(`${TILE_MEDIA}, .sg-buy__main, .sx-split__media`);
    lastAdd = photo ? { photo, at: Date.now() } : null;
  }

  // The theme may swap the bubble element itself, so remember the number,
  // not the element.
  const cartCount = () => {
    const el = document.querySelector('.cart-bubble__text-count');
    return el ? parseInt(el.textContent, 10) || 0 : 0;
  };
  let lastCount = cartCount();

  let queued = false;
  const checkCart = () => {
    queued = false;
    const now = cartCount();
    const grew = now > lastCount;
    lastCount = now;
    if (!grew || !motion.matches) return;
    document.querySelectorAll('.cart-bubble').forEach((bubble) => {
      bubble.style.setProperty('--sr-press', '0.7');
      bubble.style.setProperty('--sr-over', '1.25');
      bubble.style.setProperty('--sr-jump', '-3px');
      replay(bubble, 'sr-pop');
    });
    if (lastAdd && lastAdd.photo.isConnected && Date.now() - lastAdd.at < 8000) {
      replay(lastAdd.photo, 'sr-cheer');
    }
    lastAdd = null;
  };
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(checkCart);
  }).observe(document.body, { childList: true, characterData: true, subtree: true });
})();
